/**
 *      ▄ █
 *    █ █ █▄▄  LOADBALANCER CLIENT
 *    █ █ ▄ █
 *    ▀     ▀  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component, OnInit } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {
    LanguageService,
    LogService,
    NavigationService,
    UtilsService,
    ValidationService
} from '../../../../../../core/services';
import { RegionsApiService } from '../../../http/regions.api.service';
import { forkJoin } from 'rxjs';
import { nameValidation } from '../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lb-add-edit-region',
    templateUrl: './add-edit-region.component.html',
    styleUrls: ['./add-edit-region.component.scss']
})
export class AddEditRegionComponent extends BaseComponent implements OnInit {
    /**
     * Region form.
     */
    public regionForm: FormGroup;

    /**
     * Region id.
     */
    public regionId: string = '';

    /**
     * Store region name.
     */
    public regionName: string = '';

    /**
     * Is component embedded in wizard.
     */
    public embedded: boolean = false;

    /**
     * Store the callback function.
     */
    public callBack: (...args: any[]) => void;
    /**
     * Store page title.
     */
    public pageTitle: string = '';

    /**
     * Set the page type e.g. add/edit region, add/edit subnet.
     */
    public pageType: string;

    /**
     * Store the step count.
     */
    public step: number = 1;

    /**
     * Store current index of the subnet field.
     */
    public currentIndex: number = 0;

    /**
     * Store the value of the subnet to edit.
     */
    public subnetToEdit: string;

    /**
     * Store the subnets fetched from the API.
     * @private
     */
    private storedSubnets: string[] = [];

    /**
     * Store regions for validation.
     * @private
     */
    private regions: any[] = [];

    /**
     * Store the subnets.
     * @private
     */
    private _subnets: FormArray;

    constructor(
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private logService: LogService,
        private navigationService: NavigationService,
        private regionApiService: RegionsApiService,
        private route: ActivatedRoute,
        private router: Router,
        private utilsService: UtilsService
    ) {
        super();

        this.setDataFromNavigation();
        this.setUpFormGroup();
        this.showHideSteps();
        this.setUpFormSubscribers();
    }

    public ngOnInit(): void {
        this.setPageTitle();
        this.setUpPageData();
    }

    /**
     * Getter for subjectAlts in the Create CSR form.
     */
    get subnets(): FormArray {
        this._subnets = this.regionForm.get('step2.subnets') as FormArray;

        return this._subnets;
    }

    /**
     * Add subnet.
     */
    public addSubnet(index: number): void {
        const formField = this.regionForm.get('step2.subnets.' + index);
        if (formField.value && formField.valid) {
            this.currentIndex++;
            this.subnets.push(new FormControl(null, ValidationService.formIPCIDRSubnetValidator));
        }
    }

    /**
     * Remove subnet.
     * @param i
     */
    public removeSubnet(i: number): void {
        this.subnets.removeAt(i);
        this.checkForDuplicateSubnets();
        this.currentIndex--;
    }

    /**
     * Check for duplicate subnets.
     */
    public checkForDuplicateSubnets(): void {
        const duplicates: any[] = [];

        for (const control of this.subnets.controls) {
            if (this.pageType === 'addSubnet' || this.pageType === 'editSubnet') {
                if (this.storedSubnets.includes(control.value)) {
                    duplicates.push(control.value);
                }
            }
            if (duplicates.includes(control.value)) {
                control.setErrors({ 'duplicateValidator': `${this.languageService.translate('duplicate_subnet')}` });
            } else {
                if (control.hasError('duplicateValidator')) {
                    control.setErrors({ 'duplicateValidator': null });
                    control.updateValueAndValidity();
                }
            }
            duplicates.push(control.value);
        }
    }

    /**
     * Proceed to next step.
     */
    public nextStep(): void {
        this.step++;
    }

    /**
     * Submit region.
     */
    public submitRegion(): void {
        const formData = this.getFormattedFormData();
        const apiCall: 'editRegion' | 'addRegion' = this.regionId ? 'editRegion' : 'addRegion';
        this.regionApiService[apiCall](formData)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data): void => {
                    if (this.embedded) {
                        const newRegion = {
                            id: data.id,
                            name: formData.name
                        };
                        this.callBack(newRegion);
                    } else {
                        if (this.pageType === 'addSubnet' || this.pageType === 'editSubnet') {
                            this.router.navigateByUrl(
                                this.navigationService.getEnduranceUrl('/network/regions/' + this.regionId)).then().catch();
                        } else {
                            const path: string = this.regionId ? '../../' : '..';
                            this.router.navigate([path], { relativeTo: this.route }).then().catch();
                        }
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('AddEditRegionComponent: submitRegion',
                        'Failed to submit region', error.message);
                }
            });
    }

    /**
     * Cancel the page and navigate away.
     */
    public cancel(): void {
        if (this.embedded) {
            this.callBack();
        } else {
            this.router.navigate([this.regionId ? '../../' : '..'], { relativeTo: this.route }).then();
        }
    }

    /**
     * Go back.
     */
    public back(): void {
        this.step--;
    }

    /**
     * Returns form control as FormControl.
     * @param control
     */
    public getFormControl(control: any): FormControl {
        return control as FormControl;
    }

    /**
     * Get formatted data for submission.
     * @private
     */
    private getFormattedFormData(): any {
        let subnets: string[] = this.storedSubnets;
        if (this.subnetToEdit) {
            const subnetIndex: number = subnets.findIndex((item: string) => item === this.subnetToEdit);

            if (subnetIndex !== -1) {
                subnets.splice(subnetIndex, 1, this.regionForm.get('step2.subnets').value[0]);
            }
        }

        if (this.pageType === 'addSubnet') {
            subnets = subnets.concat(this.regionForm.get('step2.subnets').value);
        }

        if (this.pageType === 'editRegion') {
            subnets = this.regionForm.get('step2.subnets').value;
        }

        return {
            id: this.regionId || '',
            name: this.regionForm.get('step1.name').value,
            subnets: this.utilsService.cleanArray(this.regionId ? subnets : this.regionForm.get('step2.subnets').value)
        };
    }

    /**
     * Set data from navigation state.
     * @private
     */
    private setDataFromNavigation(): void {
        const navState = this.router.getCurrentNavigation()?.extras?.state;
        this.regionId = this.route.snapshot.params['id'];
        if (navState) {
            this.pageType = navState.pageType;
            this.subnetToEdit = navState.subnetToEdit;
        }
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls = {
            regions: this.regionApiService.getRegions()
        };

        if (this.regionId) {
            apiCalls['region'] = this.regionApiService.getRegion(this.regionId);
        }

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroups: any): void => {
                    if (dataGroups['regions']) {
                        this.regions = dataGroups['regions'];
                    }

                    if (dataGroups['region']) {
                        const data = dataGroups['region'];
                        this.storedSubnets = data.subnets;
                        this.regionName = data.name;
                        this.patchDataToForm(data);
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('RegionsComponent: setUpPageData',
                        'Failed to get regions data', error.message);
                }
            });
    }

    /**
     * Set page title.
     * @private
     */
    private setPageTitle(): void {
        let pageTitle: string;

        switch (this.pageType) {
            case 'addRegion':
                pageTitle = 'region_add';
                break;
            case 'editRegion':
                pageTitle = 'region_edit';
                break;
            case 'addSubnet':
                pageTitle = 'subnet_add';
                break;
            case 'editSubnet':
                pageTitle = 'subnet_edit';
                break;
        }

        this.pageTitle = pageTitle;
    }

    /**
     * Patch data to form.
     * @param data
     * @private
     */
    private patchDataToForm(data: any): void {
        this.regionForm.get('step1.name').setValue(this.regionName);
        this.regionForm.updateValueAndValidity();
        if (this.subnetToEdit) {
            this.createSubnetFormControls([this.subnetToEdit]);
            this.regionForm.get('step2.subnets').setValue([this.subnetToEdit]);
        } else {
            if (data?.subnets?.length && this.pageType === 'editRegion') {
                const subnets: string[] = [];
                for (const subnet of data.subnets) {
                    subnets.push(subnet);
                }
                this.createSubnetFormControls(subnets);
                this.regionForm.get('step2.subnets').setValue(subnets);
            }
        }
    }

    /**
     * Creates subnet form controls on edit.
     * @param array
     * @private
     */
    private createSubnetFormControls(array: string[]): void {
        for (let i = 0; i < array.length - 1; i++) {
            this.subnets.push(new FormControl(array[i], ValidationService.formIPCIDRSubnetValidator));
            this.currentIndex++;
        }
    }

    /**
     * Set up the form for the page.
     * @private
     */
    private setUpFormGroup(): void {
        this.regionForm = this.formBuilder.group({
            step1: this.formBuilder.group({
                id: [], // used for edit
                name: [null, [Validators.required, nameValidation]],
            }),
            step2: this.formBuilder.group({
                subnets: new FormArray([
                    new FormControl(null, ValidationService.formIPCIDRSubnetValidator)
                ], { validators: ValidationService.atLeastOneRequired })
            })
        });
    }

    /**
     * Show/hide steps.
     * @private
     */
    private showHideSteps(): void {
        if (this.regionId && (this.pageType === 'addSubnet' || this.pageType === 'editSubnet')) {
            this.regionForm.get('step1').disable();
            this.step = 2;
        }
    }

    /**
     * Set up form subscribers.
     * @private
     */
    private setUpFormSubscribers(): void {
        const nameField = this.regionForm.get('step1.name');
        this.regionForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (form: any): void => {
                    this.utilsService.debounce('regionForm', () => {
                        if (this.nameIsDuplicate(form.step1.name)) {
                            nameField.setErrors({
                                'duplicateLabel': `${this.languageService.translate('name')}
                            ${this.languageService.translate('already_in_use')}`
                            });
                        }
                    });
                }
            });
    }

    /**
     * Validate region name
     * @param value
     * @private
     */
    private nameIsDuplicate(value: string): boolean {
        return !!this.regions.find(item => item.name === value && item.id !== this.regionId);
    }
}
